import React, { useState, useEffect } from 'react'
import { T, Spinner, SectionLabel, SearchBar, searchMatch, probColor, TeamLogo, Sheet, Avatar, tdColor } from '../ui'
import DataGrid, { nameCell, probCell, rankColor } from '../grids'
import { getNFLMatchups, getNFLDefense, getNFLAttackProof, getNFLTdRoles } from '../api'
import { teamNick } from '../plain'

// Which defense-vs-position columns to show for each position. `yk` = yards key,
// `yr` = yards rank, `tk` = TD/g key, `tr` = TD rank. Rank 1 = allows the MOST.
const POSITIONS = [
  { key: 'qb', label: 'QB', yk: 'qb', yr: 'qbr', tk: 'qb_td', tr: 'qbtr', yl: 'Pass Yds/g' },
  { key: 'rb', label: 'RB', yk: 'rb', yr: 'rbr', tk: 'rb_td', tr: 'rbtr', yl: 'Rush Yds/g' },
  { key: 'wr', label: 'WR', yk: 'wr', yr: 'wrr', tk: 'wr_td', tr: 'wrtr', yl: 'Rec Yds/g' },
  { key: 'te', label: 'TE', yk: 'te', yr: 'ter', tk: 'te_td', tr: 'tetr', yl: 'Rec Yds/g' },
]

// value + colored league rank chip (#1 = softest, green; #32 = toughest, red)
const rankNum = (val, rk) => (
  <span>{val ?? '—'} {rk != null && <span style={{ color: rankColor(rk), fontSize: 10 }}>#{rk}</span>}</span>
)

const advCell = v => {
  const up = v === '+', down = v === '-'
  return <b style={{ color: up ? T.green : down ? T.red : T.muted }}>{up ? 'WR ▲' : down ? 'CB ▲' : 'even'}</b>
}
const MATCH_COLS = [
  { key: 'wr', label: 'Receiver', left: 1, fmt: nameCell },
  { key: 'cb', label: 'Coverage', left: 1, fmt: (v, r) => <span>{v} <span style={{ color: T.muted, fontSize: 11 }}>{r.dtm}</span></span> },
  { key: 'cov', label: 'Cov Grade', heat: { min: 55, max: 85, inv: true } },
  { key: 'sep', label: 'Sep (yds)', fmt: v => v != null ? v.toFixed(1) : '—' },
  { key: 'adv', label: 'Edge', fmt: advCell },
  { key: 'proj', label: 'Proj Rec%', fmt: probCell },
]

// ── Attack Board — the headline, scannable "who to attack & how" view. Leads with the PLAY
// (from the server's `attack` tag), shows softness as a color heat-strip by position instead of a
// number grid, and flags a leaky red zone. Green = soft (attack), red = tough — the app's
// convention, matching the detailed grid below. Softest defenses first.
const AB_AREAS = [['rbr', 'RB'], ['wr_slot_r', 'SLOT'], ['wr_deep_r', 'DEEP'], ['ter', 'TE'], ['qbr', 'QB']]
const abScore = r => {
  const v = ['rbr', 'wrr', 'ter', 'qbr'].map(k => r[k]).filter(x => x != null)
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : 99
}
// Market filter — one-up on the competitor's position toggle. Re-ranks the board by the chosen
// market's softness, re-labels the play, and swaps the tap-to-players list to that market.
const AB_FILTERS = [['all', 'All'], ['anytime_td', 'TD'], ['receptions', 'Rec'], ['rush_yards', 'Rush'], ['pass_yards', 'Pass']]
const AB_MKT_LABEL = { anytime_td: 'TD', receptions: 'receptions', rush_yards: 'rush yds', pass_yards: 'pass yds', rec_yards: 'rec yds' }
const AB_FCFG = {
  anytime_td: { rank: 'rzr', play: 'Red zone TDs', area: 'RZ TD' },
  receptions: { rank: 'wrr', play: 'Target receivers', area: 'WR' },
  rush_yards: { rank: 'rbr', play: 'Run the ball', area: 'RB' },
  pass_yards: { rank: 'qbr', play: 'Air it out', area: 'QB' },
}
function AttackBoard({ def, proof, onPlayer }) {
  const [open, setOpen] = useState(null)
  const [filt, setFilt] = useState('all')
  const cfg = filt === 'all' ? null : AB_FCFG[filt]
  // Require an opponent THIS week (r.attack.opp): the board is "who to attack this week", so a
  // defense on bye has no matchup to exploit — showing it gives a dead, un-expandable row.
  const rows = cfg
    ? [...def].filter(r => r.attack && r.attack.opp && r[cfg.rank] != null).sort((a, b) => a[cfg.rank] - b[cfg.rank]).slice(0, 12)
    : [...def].filter(r => r.attack && r.attack.opp).sort((a, b) => abScore(a) - abScore(b)).slice(0, 12)
  if (!rows.length) return null
  // Per-row display: the global softest ("All") or the chosen market's cut.
  const viewFor = (r) => cfg
    ? { play: cfg.play, market: filt, area: cfg.area, rank: r[cfg.rank], soft: r[cfg.rank] != null && r[cfg.rank] <= 12,
        opp: r.attack.opp, targets: (r.attack.targets_by_market || {})[filt] || [] }
    : r.attack
  // Lead the proof with our EDGE market (TD), not the blended number — receptions drags the
  // overall toward a coin flip and buries a genuinely strong TD record. Honest: the sub-line
  // still carries the overall + the receptions caveat.
  const bm = (proof && proof.by_market) || {}
  const td = ['anytime_td', 'pass_tds'].reduce((a, k) => {
    const d = bm[k]; if (d) { a.w += d.wins; a.l += d.losses } return a
  }, { w: 0, l: 0 })
  const tdN = td.w + td.l, tdHit = tdN ? Math.round(100 * td.w / tdN) : null
  const ov = proof && proof.overall
  return (
    <div style={{ marginBottom: 18 }}>
      <SectionLabel>Who to attack — and how</SectionLabel>
      {tdN >= 8 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, background: T.greenDim,
          border: `1px solid ${T.green}44`, borderRadius: 11, padding: '12px 14px', marginBottom: 10 }}>
          <span style={{ fontFamily: T.mono, fontSize: 26, fontWeight: 900, color: T.green, lineHeight: 1 }}>
            {td.w}&ndash;{td.l}</span>
          <span style={{ fontSize: 12.5, color: T.text, fontWeight: 600, lineHeight: 1.4 }}>
            <b style={{ color: T.green }}>{tdHit}%</b> attacking a soft defense in a <b style={{ color: T.text }}>TD market</b> — our edge.
            <span style={{ color: T.muted }}> Graded record, not a stat table.{ov && ov.plays >= 10 ? ` Across all markets: ${ov.wins}–${ov.losses}.` : ''}</span></span>
        </div>
      )}
      <div style={{ fontSize: 10.5, color: T.muted, marginBottom: 10, lineHeight: 1.45 }}>
        The softest defenses this week, by position. We lead with the <b style={{ color: T.text }}>play</b>, not a stat grid.
        Heat = softness rank · <b style={{ color: T.green }}>green = attack</b>, red = tough. <b style={{ color: T.text }}>RZ</b> flags a leaky red zone. <b style={{ color: T.text }}>Tap a row</b> for the players facing them.
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        {AB_FILTERS.map(([k, lbl]) => {
          const on = k === filt
          return (
            <button key={k} onClick={() => { setFilt(k); setOpen(null) }} style={{
              flex: 1, padding: '8px 0', borderRadius: 9, cursor: 'pointer', fontWeight: 800, fontSize: 12.5,
              border: `1px solid ${on ? T.green : T.border}`, background: on ? T.greenDim : T.card,
              color: on ? T.green : T.muted,
            }}>{lbl}</button>
          )
        })}
      </div>
      <div style={{ display: 'flex', gap: 5, padding: '0 8px 6px', fontSize: 10, color: T.faint, fontWeight: 800, letterSpacing: '.04em' }}>
        <div style={{ width: 30 }} />
        {AB_AREAS.map(([, lbl]) => <div key={lbl} style={{ width: 34, textAlign: 'center' }}>{lbl}</div>)}
        <div style={{ width: 40, textAlign: 'center' }}>RZ</div>
        <div style={{ flex: 1, paddingLeft: 6 }}>HOW TO ATTACK</div>
      </div>
      <div style={{ background: T.card2, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden' }}>
        {rows.map((r, i) => {
          const v = viewFor(r)
          const rz = r.rzr, rzHot = rz != null && rz <= 8
          const tgts = v.targets || []
          const canOpen = tgts.length > 0
          const isOpen = open === r.team
          return (
            <div key={r.team} style={{ borderTop: i ? `1px solid ${T.border}` : 'none' }}>
              <div onClick={() => canOpen && setOpen(isOpen ? null : r.team)}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '9px 8px',
                  cursor: canOpen ? 'pointer' : 'default', background: isOpen ? T.card : 'transparent' }}>
                <div title={teamNick(r.team)} style={{ width: 30, display: 'flex', justifyContent: 'center' }}><TeamLogo abbr={r.team} size={24} /></div>
                {AB_AREAS.map(([k]) => {
                  const rk = r[k], col = rk != null ? rankColor(rk) : T.faint
                  return (
                    <div key={k} style={{ width: 34, height: 30, borderRadius: 7, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', fontFamily: T.mono, fontSize: 12.5, fontWeight: 800,
                      background: rk != null ? col + '26' : 'transparent', color: rk != null ? col : T.faint,
                      border: `1px solid ${rk != null ? col + '44' : T.border}` }}>{rk ?? '–'}</div>
                  )
                })}
                <div style={{ width: 40, textAlign: 'center', fontFamily: T.mono, fontSize: 10.5, fontWeight: 800,
                  color: rzHot ? T.green : T.faint }}>{rz != null ? `#${rz}` : '–'}</div>
                <div style={{ flex: 1, paddingLeft: 6, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 900, color: v.soft ? T.green : T.muted,
                    lineHeight: 1.15, hyphens: 'none', overflowWrap: 'normal' }}>
                    {v.play}{canOpen && <span style={{ color: T.faint, fontWeight: 700, fontSize: 11 }}> {isOpen ? '▾' : '›'}</span>}
                  </div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.faint }}>
                    softest: {v.area} · #{v.rank}{v.opp ? ` · vs ${teamNick(v.opp)}` : ''}
                  </div>
                </div>
              </div>
              {isOpen && (
                <div style={{ padding: '2px 8px 10px 42px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <div style={{ fontSize: 10, color: T.faint, fontWeight: 800, letterSpacing: '.04em' }}>
                      {v.opp} PLAYERS TO {v.play.toUpperCase()}
                    </div>
                    {proof && proof.by_market && proof.by_market[v.market] && proof.by_market[v.market].plays >= 4 && (() => {
                      // Name the market and color the record by how it's actually doing, so a
                      // losing receptions record never reads as a green endorsement.
                      const pm = proof.by_market[v.market], n = pm.wins + pm.losses
                      const hit = n ? pm.wins / n : 0
                      return (
                        <div style={{ fontSize: 10, fontWeight: 800, color: T.muted }}>
                          our {AB_MKT_LABEL[v.market] || 'soft-D'} record: <b style={{
                            color: hit >= 0.55 ? T.green : hit >= 0.5 ? T.text : T.amber }}>{pm.wins}&ndash;{pm.losses}</b>
                        </div>
                      )
                    })()}
                  </div>
                  {tgts.map(t => (
                    <div key={t.gsis_id || t.name} onClick={() => onPlayer && onPlayer({
                      full_name: t.name, pos: t.pos, team: v.opp, opp: r.team,
                      ...(t.gsis_id ? { gsis_id: t.gsis_id } : {}), status: 'active' })}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        background: T.card2, border: `1px solid ${T.border}`, borderRadius: 8,
                        padding: '8px 11px', cursor: 'pointer' }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: T.text, display: 'flex', alignItems: 'center', gap: 7 }}>
                        {t.name}
                        <span style={{ color: T.faint, fontWeight: 700, fontSize: 10.5 }}>{t.pos}</span>
                        {t.rec && <span style={{ fontSize: 10, fontWeight: 900, color: T.green, background: T.greenDim,
                          border: `1px solid ${T.green}55`, borderRadius: 999, padding: '2px 7px', letterSpacing: '.03em' }}>★ ON OUR BOARD</span>}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        {t.line != null && (
                          <span style={{ fontFamily: T.mono, fontSize: 10.5, fontWeight: 700, color: T.faint }}>
                            {v.market === 'anytime_td' ? 'TD' : `o${t.line}`}
                          </span>
                        )}
                        {/* Honest P(over the OFFERED line), colored by value — a sub-coinflip prop
                            reads amber/red, never a flat green, so a −EV target can't look like a lock. */}
                        <span style={{ fontFamily: T.mono, fontSize: 13, fontWeight: 900, color: probColor((t.prob || 0) / 100) }}>{t.prob}%</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Segmented position selector.
function PosToggle({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
      {POSITIONS.map(p => {
        const on = p.key === value
        return (
          <button key={p.key} onClick={() => onChange(p.key)} style={{
            flex: 1, padding: '9px 0', borderRadius: 9, cursor: 'pointer', fontWeight: 800, fontSize: 13,
            border: `1px solid ${on ? T.green : T.border}`, background: on ? T.greenDim : T.card,
            color: on ? T.green : T.muted,
          }}>{p.label}</button>
        )
      })}
    </div>
  )
}

// WR target-depth views (free slot-vs-outside proxy): slot = underneath (≤8 air
// yds), outside = perimeter/deep. Overrides the WR column keys when selected.
const WR_DEPTH = {
  all:     { yk: 'wr', yr: 'wrr', tk: 'wr_td', tr: 'wrtr', yl: 'Rec Yds/g' },
  slot:    { yk: 'wr_slot', yr: 'wr_slot_r', tk: 'wr_slot_td', tr: 'wr_slot_tr', yl: 'Slot Yds/g' },
  outside: { yk: 'wr_deep', yr: 'wr_deep_r', tk: 'wr_deep_td', tr: 'wr_deep_tr', yl: 'Outside Yds/g' },
}

// QB passing vs rushing split — rushing is the goal-line rush D a mobile QB faces (drives
// the QB-rushing Pay Dirt angle), where the pass-defense number is misleading.
const QB_SPLIT = {
  pass: { yk: 'qb', yr: 'qbr', tk: 'qb_td', tr: 'qbtr', yl: 'Pass Yds/g' },
  rush: { yk: 'qb_rush', yr: 'qb_rush_r', tk: 'qb_rush_td', tr: 'qb_rush_tr', yl: 'Rush Yds/g' },
}

export default function MatchupsTab({ season, week, onPlayer }) {
  const [match, setMatch] = useState(null)
  const [def, setDef] = useState(null)
  const [proof, setProof] = useState(null)
  const [sample, setSample] = useState(false)
  const [pos, setPos] = useState('wr')
  const [hasDepth, setHasDepth] = useState(false)
  const [wrDepth, setWrDepth] = useState('all')   // all | slot | outside
  const [hasQbRush, setHasQbRush] = useState(false)
  const [qbSplit, setQbSplit] = useState('pass')  // pass | rush
  const [q, setQ] = useState('')
  // Who each defense faces this week + the players attacking it (TD-by-role table), so tapping
  // "DET is soft to TEs" answers "so who gets them?" — e.g. Tucker Kraft.
  const [attackers, setAttackers] = useState([])
  const [selDef, setSelDef] = useState(null)

  useEffect(() => {
    let alive = true
    getNFLTdRoles(season, week).then(r => { if (alive) setAttackers(r?.players || []) })
    return () => { alive = false }
  }, [season, week])

  useEffect(() => {
    let alive = true
    Promise.all([getNFLMatchups(season, week), getNFLDefense(season, week), getNFLAttackProof(season)]).then(([m, d, p]) => {
      if (!alive) return
      setMatch(m.rows || []); setDef(d.rows || []); setProof(p || null); setSample(!!m._sample)
      setHasDepth(!!d.has_wr_depth); setHasQbRush(!!d.has_qb_rush)
    })
    return () => { alive = false }
  }, [season, week])

  if (!match || !def) return <Spinner />

  const showDepth = pos === 'wr' && hasDepth
  const showQbSplit = pos === 'qb' && hasQbRush
  const base = POSITIONS.find(p => p.key === pos)
  const P = showDepth ? { ...base, ...WR_DEPTH[wrDepth] }
    : (showQbSplit && qbSplit === 'rush') ? { ...base, ...QB_SPLIT.rush }
    : base
  const facesOf = {}
  for (const a of attackers) if (a.opp && a.team && !facesOf[a.opp]) facesOf[a.opp] = a.team
  const defRows = (def || []).map(r => ({ ...r, faces: facesOf[r.team] || null }))
  const POS_UP = { qb: 'QB', rb: 'RB', wr: 'WR', te: 'TE' }[pos]
  const targets = selDef ? attackers.filter(a => a.opp === selDef.team && a.pos === POS_UP)
    .sort((x, y) => (y.model_td_prob || 0) - (x.model_td_prob || 0)) : []
  const ROLE = { WR1: 'No. 1 WR', WR2: 'No. 2 WR', WR3: 'No. 3 WR', WR_OTHER: 'depth WR', TE1: 'starting TE',
    TE2: 'backup TE', TE_OTHER: 'depth TE', RB1: 'lead RB', RB2: 'backup RB', QB: 'QB' }
  const DEF_COLS = [
    { key: 'team', label: 'Defense', left: 1, fmt: (v, r) => (
      <span><b style={{ color: T.text }}>{v}</b>
        <span style={{ display: 'block', fontSize: 10.5, color: r.faces ? T.muted2 : T.faint, fontWeight: 600 }}>
          {r.faces ? `vs ${teamNick(r.faces)}` : 'bye'}</span></span>) },
    { key: P.yk, label: P.yl, heat: { min: 1, max: 32, inv: true }, heatVal: r => r[P.yr], fmt: (v, r) => rankNum(v, r[P.yr]) },
    { key: P.tk, label: 'TD/g allowed', heat: { min: 1, max: 32, inv: true }, heatVal: r => r[P.tr], fmt: (v, r) => rankNum(v, r[P.tr]) },
    // Red-zone TD rate allowed — the share of trips inside the 20 that end in a TD.
    // Team-level (who bends when the field shrinks); the single biggest TD-prop tell.
    { key: 'rz_td_rate', label: 'RZ TD%', heat: { min: 1, max: 32, inv: true }, heatVal: r => r.rzr,
      fmt: (v, r) => rankNum(v != null ? `${Math.round(v * 100)}%` : null, r.rzr) },
  ]

  return (
    <div style={{ padding: '6px 16px 80px' }}>
      <AttackBoard def={def} proof={proof} onPlayer={onPlayer} />
      <SectionLabel>Defense vs position — who gives up the most</SectionLabel>
      <div style={{ fontSize: 10.5, color: T.muted, marginBottom: 9 }}>
        Yards & TDs allowed per game, plus <b style={{ color: T.text }}>RZ TD%</b> — the share of red-zone trips a defense lets score. <b style={{ color: T.green }}>#1 = softest</b> (allows the most) · green = attack, red = avoid. Tap a column to re-sort.
      </div>
      <PosToggle value={pos} onChange={setPos} />
      {showQbSplit && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
          {[['pass', 'Passing'], ['rush', 'Rushing (goal-line)']].map(([k, lbl]) => {
            const on = k === qbSplit
            return (
              <button key={k} onClick={() => setQbSplit(k)} style={{
                flex: 1, padding: '7px 0', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 11.5,
                border: `1px solid ${on ? T.mint : T.border}`, background: on ? T.card2 : T.card,
                color: on ? T.mint : T.muted,
              }}>{lbl}</button>
            )
          })}
        </div>
      )}
      {showQbSplit && qbSplit === 'rush' && (
        <div style={{ fontSize: 10, color: T.muted, marginBottom: 8, lineHeight: 1.4 }}>
          QB <b style={{ color: T.text }}>rushing</b> yards + rush TDs allowed — the goal-line run defense a mobile QB (sneaks, tush-push, scrambles) faces. #1 = softest.
        </div>
      )}
      {showDepth && (<>
        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          {[['all', 'All WR'], ['slot', 'Slot (underneath)'], ['outside', 'Outside (deep)']].map(([k, lbl]) => {
            const on = k === wrDepth
            return (
              <button key={k} onClick={() => setWrDepth(k)} style={{
                flex: 1, padding: '7px 0', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 11.5,
                border: `1px solid ${on ? T.mint : T.border}`, background: on ? T.card2 : T.card,
                color: on ? T.mint : T.muted,
              }}>{lbl}</button>
            )
          })}
        </div>
        {wrDepth !== 'all' && (
          <div style={{ fontSize: 10, color: T.muted, marginBottom: 8, lineHeight: 1.4 }}>
            Target-depth proxy: <b style={{ color: T.text }}>slot</b> = underneath targets (≤8 air yds), <b style={{ color: T.text }}>outside</b> = perimeter/deep (&gt;8). True line-up alignment needs PFF; this splits by where the ball goes.
          </div>
        )}
      </>)}
      <div style={{ fontSize: 10.5, color: T.muted, margin: '-2px 0 6px' }}>Tap a defense to see who gets them this week.</div>
      <DataGrid cols={DEF_COLS} rows={defRows} initialSort={P.yk} minWidth={430} onRow={r => setSelDef(r)} />
      {selDef && (
        <Sheet onClose={() => setSelDef(null)}>
          <div style={{ padding: '6px 16px 24px' }}>
            <div style={{ fontSize: 18, fontWeight: 900 }}>{teamNick(selDef.team)} defense vs {POS_UP}s</div>
            <div style={{ fontSize: 12, color: T.muted2, marginTop: 4, lineHeight: 1.45 }}>
              {selDef[P.yk] ?? '—'} {P.yl.toLowerCase()} allowed{selDef[P.yr] ? ` (#${selDef[P.yr]})` : ''} ·{' '}
              {selDef[P.tk] ?? '—'} TDs/game allowed{selDef[P.tr] ? ` (#${selDef[P.tr]})` : ''} · #1 = softest</div>
            <div style={{ fontSize: 13, fontWeight: 800, marginTop: 12 }}>
              {selDef.faces ? `This week: ${teamNick(selDef.faces)} ${POS_UP}s get them` : 'No game this week'}</div>
            {selDef.faces && (targets.length ? targets.map(a => (
              <button key={a.gsis_id} onClick={() => { setSelDef(null); onPlayer && onPlayer({ full_name: a.name, gsis_id: a.gsis_id,
                pos: a.pos, team: a.team, opp: a.opp, headshot: a.headshot, status: 'active', market: 'Anytime TD', prop_type: 'anytime_td' }) }}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', cursor: 'pointer', color: T.text,
                  background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: '9px 10px', marginTop: 6 }}>
                <Avatar url={a.headshot} name={a.name} team={a.team} size={30} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13.5, fontWeight: 800 }}>{a.name}</span>
                  <span style={{ display: 'block', fontSize: 11, color: T.muted }}>{ROLE[a.role] || a.role_label || a.role}
                    {a.opp_rank ? ` · #${a.opp_rank} matchup for his role` : ''}</span>
                </span>
                {a.model_td_prob != null && (
                  <span style={{ textAlign: 'right', flexShrink: 0 }}>
                    <span style={{ display: 'block', fontFamily: T.mono, fontSize: 15, fontWeight: 900, color: tdColor(a.model_td_prob) }}>
                      {Math.round(a.model_td_prob * 100)}%</span>
                    <span style={{ display: 'block', fontSize: 9.5, color: T.muted }}>TD chance</span>
                  </span>
                )}
              </button>
            )) : <div style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>No {POS_UP}s on the board for this game yet.</div>)}
            <div style={{ fontSize: 9.5, color: T.faint, marginTop: 12 }}>Research, not advice · tap a player for the full breakdown</div>
          </div>
        </Sheet>
      )}

      <SectionLabel>Receiver vs pass defense — coverage matchup</SectionLabel>
      <div style={{ fontSize: 10, color: T.muted, marginBottom: 8 }}>Opp defense-vs-WR rank + NGS separation · WR ▲ = favorable</div>
      {sample && <div style={{ fontSize: 10, color: T.amber, marginBottom: 8 }}>● Live data unavailable right now</div>}
      {match.length > 0 && <SearchBar value={q} onChange={setQ} placeholder="Search receiver, team, or opponent…" />}
      <DataGrid cols={MATCH_COLS} rows={match.filter(searchMatch(q, r => [r.wr, r.team, r.dtm]))} initialSort="proj"
        onRow={r => onPlayer && onPlayer({ full_name: r.wr, pos: 'WR', team: r.team, opp: r.dtm,
          // matchup 'proj' is a coverage score, not a bet probability — don't pass it as
          // one (it opened a fake bet card). Open the player-info view instead.
          ...(r.gsis_id ? { gsis_id: r.gsis_id } : {}),
          note: `vs ${r.cb} (${r.dtm})`, status: 'active' })} />
    </div>
  )
}
